/**
 * The selection — `KLQ-20260825-006` §B, modelled on ayon-frontend's
 * `shared/src/containers/ProjectTreeTable/context/SelectionCellsProvider.tsx`.
 *
 * **It owns "which cells", and nothing else.** Not what a cell means, not what may be done to
 * one, not how anything is drawn. Two mechanisms sit on top of it and neither is allowed in
 * here: `KLQ-20260828-001` (editing) reads `cells` and writes; `KLQ-20260824-002` (clipboard)
 * reads `cells` and serialises. The reason for the discipline is ayon's own layering — its
 * `ClipboardContext` imports `useCellEditing` rather than owning a write — and the failure it
 * avoids is two mechanisms each growing half a selection model.
 *
 * **Three pieces of state, and every gesture is a function of them** (§B.1): the set of
 * selected cells, an `anchor` (the corner a rectangle is measured from) and a `focus` (where
 * the keyboard is). Ayon keeps exactly these three, and the reason to name the anchor
 * separately from "the last thing selected" is that a selection has no direction and an
 * anchor does — it is what lets Shift widen *and* narrow the same range.
 *
 * **Two dialects, separated by which column the pointer is in** (§B.8). A click on a data
 * cell *replaces* — it is a statement about where I am, and looking somewhere new ends looking
 * at the old place. A click in the select column *toggles* — it is a statement about one row,
 * made while looking elsewhere, and a tick that clears eleven other ticks is not a tick. Ayon
 * routes its selection column through the ordinary cell path and therefore replaces
 * (`SelectionCellsProvider.tsx:168`); this is the one place we deliberately diverge, because
 * the control exists precisely for building a scattered set and that use would be impossible.
 *
 * **Rendered order is the only order** (§B.3). Ranges are computed over the grid the table
 * actually drew — collapsed branches are not in it, hidden columns are not in it — so a
 * shift-click across a closed floor cannot quietly pick up the thirty tasks beneath it.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

import { assertColKey, cellKey, cellRef, SELECT_COLUMN_KEY } from "./cell";
import type { CellKey, CellRef } from "./cell";

/** What a gesture carries. `additive` is Cmd on a Mac and Ctrl everywhere else. */
export interface Modifiers {
  readonly shift: boolean;
  readonly additive: boolean;
}

export interface SelectionApi {
  readonly cells: ReadonlySet<CellKey>;
  /**
   * §B.7 — the projection every consumer above the selection reads: the rows in which *any*
   * cell is selected, deduplicated, in rendered order. It is what let the menu, the actions
   * and the bulk endpoint survive the axis widening from rows to cells untouched, and it is
   * the honest reading of a half-selected row: somebody who selected three `when` cells and
   * pressed *Close* meant those three tasks.
   */
  readonly rows: readonly string[];
  readonly anchor: CellRef | null;
  readonly focus: CellRef | null;

  readonly isSelected: (ref: CellRef) => boolean;
  readonly isRowSelected: (rowId: string) => boolean;
  /** Every rendered, selectable row is in the selection. Drives the header's tri-state. */
  readonly allRowsSelected: boolean;
  readonly someRowsSelected: boolean;

  /** A press on a data cell. §B.2. */
  readonly press: (ref: CellRef, modifiers: Modifiers) => void;
  /** A press in the select column. §B.8 — toggles, never replaces. */
  readonly pressRow: (rowId: string, modifiers: Modifiers) => void;
  /** Pointer entered a cell with the button held. §B.2's drag row. */
  readonly drag: (ref: CellRef) => void;
  readonly endDrag: () => void;
  readonly moveFocus: (ref: CellRef, extend: boolean) => void;
  readonly selectAll: () => void;
  readonly clear: () => void;
  /** The header's select-all. Nothing selected → every rendered row; otherwise → clear. */
  readonly toggleAllRows: () => void;

  /**
   * The table tells the selection what it drew, in rendered order. Called on every render;
   * cheap when nothing changed, and the place where §B.4 happens — a row that vanished from
   * the drawn set leaves the selection, because a task a poll removed from the page cannot be
   * acted on and holding its id would mean acting on something nobody can see.
   */
  readonly registerGrid: (
    rowIds: readonly string[],
    colKeys: readonly string[],
    /**
     * The rows that may actually be **ticked**, when that is narrower than the grid
     * (`KLQ-20260828-003`). A container level takes part in the grid — the arrows reach it and
     * an editor opens on it — and is still not a subject of a bulk action, so *select all* and
     * the header's tri-state count these and not `rowIds`. Omitted means the two are the same,
     * which is every surface that has not split them.
     */
    tickIds?: readonly string[],
  ) => void;
}

const SelectionContext = createContext<SelectionApi | null>(null);

/**
 * Opt-in, per surface (§B.6). A table with no provider above it gets today's table, with no
 * selection code paths and no `aria-multiselectable` — the primitive does not fork into a
 * selectable and a non-selectable version.
 */
export function useOptionalSelection(): SelectionApi | null {
  return useContext(SelectionContext);
}

export function useSelection(): SelectionApi {
  const api = useContext(SelectionContext);
  if (api === null) {
    throw new Error("useSelection must be used within a <SelectionProvider>");
  }
  return api;
}

interface Grid {
  readonly rowIds: readonly string[];
  readonly colKeys: readonly string[];
  /** A subset of `rowIds`: the ones a tick may reach. See `registerGrid`. */
  readonly tickIds: readonly string[];
  readonly rowIndex: ReadonlyMap<string, number>;
  readonly colIndex: ReadonlyMap<string, number>;
}

const EMPTY_GRID: Grid = {
  rowIds: [],
  colKeys: [],
  tickIds: [],
  rowIndex: new Map(),
  colIndex: new Map(),
};

/** Every cell of one row, select column included — what "the row is selected" means. */
function rowCells(grid: Grid, rowId: string): CellKey[] {
  return grid.colKeys.map((colKey) => cellKey({ rowId, colKey }));
}

/**
 * The closed rectangle between two cells, in rendered order — rows by row index, columns by
 * column index, both inclusive. A cell whose row or column the table is not currently drawing
 * has no index and contributes nothing, which is §B.3 falling out of the data rather than
 * being enforced on top of it.
 */
function rectangle(grid: Grid, from: CellRef, to: CellRef): CellKey[] {
  const r1 = grid.rowIndex.get(from.rowId);
  const r2 = grid.rowIndex.get(to.rowId);
  const c1 = grid.colIndex.get(from.colKey);
  const c2 = grid.colIndex.get(to.colKey);
  if (
    r1 === undefined ||
    r2 === undefined ||
    c1 === undefined ||
    c2 === undefined
  ) {
    return [cellKey(to)];
  }
  const out: CellKey[] = [];
  for (let r = Math.min(r1, r2); r <= Math.max(r1, r2); r += 1) {
    const rowId = grid.rowIds[r];
    if (rowId === undefined) continue;
    for (let c = Math.min(c1, c2); c <= Math.max(c1, c2); c += 1) {
      const colKey = grid.colKeys[c];
      if (colKey === undefined) continue;
      out.push(cellKey({ rowId, colKey }));
    }
  }
  return out;
}

/** Every cell of every row between two rows — the select column's Shift (§B.8). */
function rowRange(grid: Grid, fromRow: string, toRow: string): CellKey[] {
  const r1 = grid.rowIndex.get(fromRow);
  const r2 = grid.rowIndex.get(toRow);
  if (r1 === undefined || r2 === undefined) return rowCells(grid, toRow);
  const out: CellKey[] = [];
  for (let r = Math.min(r1, r2); r <= Math.max(r1, r2); r += 1) {
    const rowId = grid.rowIds[r];
    if (rowId !== undefined) out.push(...rowCells(grid, rowId));
  }
  return out;
}

export function SelectionProvider({
  children,
}: {
  readonly children: ReactNode;
}) {
  const [cells, setCells] = useState<ReadonlySet<CellKey>>(new Set());
  const [anchor, setAnchor] = useState<CellRef | null>(null);
  const [focus, setFocus] = useState<CellRef | null>(null);
  const [grid, setGrid] = useState<Grid>(EMPTY_GRID);

  // A drag is a gesture, not state anybody renders: keeping it in refs means moving the
  // pointer across 40 rows does not re-render the table 40 times for a value nothing reads.
  const dragging = useRef(false);
  // Which way this drag paints. Ayon's `initialCellSelected` (`:57`): a drag that *began* on
  // something already selected removes as it goes, so dragging back over your own selection
  // undoes it instead of re-adding what is already there.
  const dragRemoves = useRef(false);
  const dragRows = useRef(false);

  const registerGrid = useCallback(
    (
      rowIds: readonly string[],
      colKeys: readonly string[],
      tickIds?: readonly string[],
    ) => {
      const ticks = tickIds ?? rowIds;
      setGrid((current) => {
        const same =
          current.rowIds.length === rowIds.length &&
          current.colKeys.length === colKeys.length &&
          current.tickIds.length === ticks.length &&
          current.rowIds.every((id, i) => id === rowIds[i]) &&
          current.colKeys.every((key, i) => key === colKeys[i]) &&
          current.tickIds.every((id, i) => id === ticks[i]);
        if (same) return current;
        colKeys.forEach(assertColKey);
        return {
          rowIds,
          colKeys,
          tickIds: ticks,
          rowIndex: new Map(rowIds.map((id, i) => [id, i])),
          colIndex: new Map(colKeys.map((key, i) => [key, i])),
        };
      });
    },
    [],
  );

  // §B.4 — the selection survives a re-render, not a refetch. Derived rather than stored, so
  // a row that comes back (a branch reopened, a page reloaded) is still selected: dropping it
  // from state on the way out would make expansion destructive, which is not what a person
  // means by collapsing a floor.
  const visible = useMemo(() => {
    if (grid.rowIds.length === 0) return new Set<CellKey>();
    const out = new Set<CellKey>();
    for (const key of cells) {
      if (grid.rowIndex.has(cellRef(key).rowId)) out.add(key);
    }
    return out;
  }, [cells, grid]);

  const rows = useMemo(() => {
    const seen = new Set<string>();
    for (const key of visible) seen.add(cellRef(key).rowId);
    // **`tickIds`, so "the selected rows" keeps meaning what it meant before the grid grew
    // wider than the tick** (`KLQ-20260828-003`). A container joined the grid so its own cells
    // could be edited; clicking one of them must not arm the bulk bar with the floor's
    // descendants, because nobody ticked anything. The cell fan-out reads `isSelected`
    // directly and is unaffected — this list is the *tick* answer, and ticking is narrow.
    return grid.tickIds.filter((id) => seen.has(id));
  }, [visible, grid]);

  const isRowSelected = useCallback(
    (rowId: string) =>
      visible.has(cellKey({ rowId, colKey: SELECT_COLUMN_KEY })),
    [visible],
  );

  const apply = useCallback(
    (
      next: Iterable<CellKey>,
      nextAnchor: CellRef | null,
      nextFocus: CellRef | null,
    ) => {
      setCells(new Set(next));
      if (nextAnchor !== null) setAnchor(nextAnchor);
      setFocus(nextFocus);
    },
    [],
  );

  const press = useCallback(
    (ref: CellRef, modifiers: Modifiers) => {
      dragging.current = true;
      dragRows.current = false;
      dragRemoves.current = visible.has(cellKey(ref));

      if (modifiers.shift && anchor !== null) {
        const range = rectangle(grid, anchor, ref);
        // The anchor deliberately stays where it is, so the same range can be widened and
        // narrowed by repeated shift-clicks rather than crawling after the pointer.
        apply(modifiers.additive ? [...visible, ...range] : range, anchor, ref);
        return;
      }

      if (modifiers.additive) {
        const next = new Set(visible);
        const key = cellKey(ref);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        apply(next, ref, ref);
        return;
      }

      // Plain click replaces — except on the one cell that is already the whole selection,
      // where replacing is a no-op and clearing is the only way back out of a mis-click.
      if (visible.size === 1 && visible.has(cellKey(ref))) {
        setCells(new Set());
        setAnchor(null);
        setFocus(null);
        return;
      }
      apply([cellKey(ref)], ref, ref);
    },
    [anchor, apply, grid, visible],
  );

  const pressRow = useCallback(
    (rowId: string, modifiers: Modifiers) => {
      dragging.current = true;
      dragRows.current = true;
      dragRemoves.current = isRowSelected(rowId);

      const here: CellRef = { rowId, colKey: SELECT_COLUMN_KEY };

      // Shift adds the interval. It never replaces — see the module note: the whole purpose of
      // this column is accumulating a set, and an interval that cleared the ticks either side
      // of it would be the same mistake as a replacing click, one gesture larger.
      if (modifiers.shift && anchor !== null) {
        apply(
          [...visible, ...rowRange(grid, anchor.rowId, rowId)],
          anchor,
          here,
        );
        return;
      }

      // Plain and Cmd/Ctrl are the same act here: the modifier is already the default.
      const next = new Set(visible);
      const own = rowCells(grid, rowId);
      if (dragRemoves.current) for (const key of own) next.delete(key);
      else for (const key of own) next.add(key);
      apply(next, here, here);
    },
    [anchor, apply, grid, isRowSelected, visible],
  );

  const drag = useCallback(
    (ref: CellRef) => {
      if (!dragging.current) return;

      if (dragRows.current) {
        const next = new Set(visible);
        const own = rowCells(grid, ref.rowId);
        if (dragRemoves.current) for (const key of own) next.delete(key);
        else for (const key of own) next.add(key);
        setCells(next);
        setFocus({ rowId: ref.rowId, colKey: SELECT_COLUMN_KEY });
        return;
      }

      if (anchor === null) return;
      setCells(new Set(rectangle(grid, anchor, ref)));
      setFocus(ref);
    },
    [anchor, grid, visible],
  );

  const endDrag = useCallback(() => {
    dragging.current = false;
  }, []);

  const moveFocus = useCallback(
    (ref: CellRef, extend: boolean) => {
      if (extend && anchor !== null) {
        apply(rectangle(grid, anchor, ref), anchor, ref);
        return;
      }
      apply([cellKey(ref)], ref, ref);
    },
    [anchor, apply, grid],
  );

  const selectAll = useCallback(() => {
    const all: CellKey[] = [];
    // `tickIds`, not `rowIds`: *select all* means every row a bulk action could act on, and a
    // container that is only in the grid so its cells can be edited is not one of them. Before
    // the two lists were separate this distinction did not exist to get wrong.
    for (const rowId of grid.tickIds) all.push(...rowCells(grid, rowId));
    setCells(new Set(all));
    // Anchor and focus are left where the person put them: select-all is about the set, and
    // moving the keyboard to row 1 would lose the place they were reading.
  }, [grid]);

  const clear = useCallback(() => {
    setCells(new Set());
    setAnchor(null);
    setFocus(null);
  }, []);

  const allRowsSelected =
    grid.tickIds.length > 0 && rows.length === grid.tickIds.length;
  const someRowsSelected = rows.length > 0 && !allRowsSelected;

  const toggleAllRows = useCallback(() => {
    if (rows.length > 0) clear();
    else selectAll();
  }, [clear, rows.length, selectAll]);

  const api = useMemo<SelectionApi>(
    () => ({
      cells: visible,
      rows,
      anchor,
      focus,
      isSelected: (ref) => visible.has(cellKey(ref)),
      isRowSelected,
      allRowsSelected,
      someRowsSelected,
      press,
      pressRow,
      drag,
      endDrag,
      moveFocus,
      selectAll,
      clear,
      toggleAllRows,
      registerGrid,
    }),
    [
      allRowsSelected,
      anchor,
      clear,
      drag,
      endDrag,
      focus,
      isRowSelected,
      moveFocus,
      press,
      pressRow,
      registerGrid,
      rows,
      selectAll,
      someRowsSelected,
      toggleAllRows,
      visible,
    ],
  );

  return (
    <SelectionContext.Provider value={api}>
      {children}
    </SelectionContext.Provider>
  );
}
